import * as yup from "yup";

export const signInSchema = yup.object({
  email: yup
    .string()
    .email("Email must be valid")
    .required("Email is required"),
  password: yup.string().required("Password is required"),
});

export const signUpSchema = yup.object({
  firstName: yup
    .string()
    .required("This field is required")
    .min(2, "Must be at least 2 characters")
    .max(32, "Must be no more than 32 characters")
    .matches(
      /^[\S]+(?:\s[\S]+)*$/,
      "First name cannot start or end with whitespace characters"
    )
    .matches(/^[^!@#$%^&*()]+$/, "Special characters are not allowed"),
  lastName: yup
    .string()
    .required("This field is required")
    .min(2, "Must be at least 2 characters")
    .max(32, "Must be no more than 32 characters")
    .matches(
      /^[\S]+(?:\s[\S]+)*$/,
      "Last name cannot start or end with whitespace characters"
    )
    .matches(/^[^!@#$%^&*()]+$/, "Special characters are not allowed"),
  email: yup
    .string()
    .email("Email must be valid")
    .required("This field is required"),
  password: yup
    .string()
    .required("required")
    .matches(/^.*[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?].*$/, "special_character")
    .matches(/^.*[0-9].*$/, "number")
    .matches(/^.*[a-z].*$/, "lowercase")
    .matches(/^.*[A-Z].*$/, "uppercase")
    .matches(/^\S*$/, "spaces")
    .matches(/^.{7,}$/, "min"),
  repeatPassword: yup
    .string()
    .required("Password confirmation is required")
    .oneOf([yup.ref("password"), null], "Passwords must match"),
  phoneNumber: yup
    .string()
    .matches(/^(?:\s*|[0-9](?: *\d| *)*)$/, "Phone number is not valid")
    .min(2, "Phone number should be at least 2 characters")
    .required("This field is required")
    .when("phoneCountryCode", {
      is: "GB",
      then: yup
        .string()
        .required("This field is required")
        .min(2, "Phone number should be at least 2 characters")
        .matches(
          /^(?:(?=\s*0)\d(?: *\d| *){10}|(?!\s*0)\d(?: *\d| *){9})?$/,
          "Phone number length exceeded"
        ),
    }),
  phoneCode: yup.string(),
  phoneCountryCode: yup.string(),
});

export const signUpInviteSchema = yup.object({
  password: yup
    .string()
    .required("required")
    .matches(/^.*[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?].*$/, "special_character")
    .matches(/^.*[0-9].*$/, "number")
    .matches(/^.*[a-z].*$/, "lowercase")
    .matches(/^.*[A-Z].*$/, "uppercase")
    .matches(/^\S*$/, "spaces")
    .matches(/^.{7,}$/, "min"),
  repeatPassword: yup
    .string()
    .required("Password confirmation is required")
    .oneOf([yup.ref("password"), null], "Passwords must match"),
});

export const additionalInfoSchema = yup.object({
  phoneNumber: yup.object({
    code: yup.string(),
    number: yup
      .string()
      .notRequired()
      .matches(/^(\s*|\d+)$/, "Phone number is not valid")
      .when("code", {
        is: "+44",
        then: yup
          .string()
          .matches(/^(0?\d{0,10})$/, "Phone number length exceeded"),
      }),
  }),
  job: yup.string().required("This field is required"),
});

const countryStructureSchema = {
  country: yup.string().required("This field is required"),
  businessStructure: yup.string().required("This field is required"),
};

export const addressValidationSchema = {
  addressLine1: yup
    .string()
    .required("This field is required")
    .test("addressLine2", "Invalid address format", (val: any) => {
      if (/^(((^([#1-9]{1,}[0-9 ]+))|(^[a-zA-Z]+))[\w .,-]*)?$/.test(val)) {
        return true;
      } else {
        return false;
      }
    }),
  addressLine2: yup
    .string()
    .test("addressLine2", "Invalid address format", (val: any) => {
      if (
        val === "" ||
        /^(((^([#1-9]{1,}[0-9 ]+))|(^[a-zA-Z]+))[\w .,-]*)?$/.test(val)
      ) {
        return true;
      } else {
        return false;
      }
    }),
  city: yup
    .string()
    .required("This field is required")
    .matches(
      /^[\S]+(?:\s[\S]+)*$/,
      "Cannot start or end with whitespace characters"
    )
    .matches(/^[^!@$%^&*()]+$/, "Special characters are not allowed"),
  zipCode: yup
    .string()
    .required("This field is required")
    .min(2, "Post code must be at least 2 characters")
    .matches(
      /^[\S]+(?:\s[\S]+)*$/,
      "Cannot start or end with whitespace characters"
    )
    .matches(/^[^!$%^"№;:?@#&*()_+]+$/, "Special characters are not allowed"),
};

export const limitedCompanySchema = yup.object({
  ...countryStructureSchema,
  limitedCompany: yup.object({
    countryCode: yup.string(),
    crn: yup.string().when("countryCode", {
      is: "GB",
      then: yup
        .string()
        .min(8, "Enter valid CRN")
        .max(8, "Enter valid CRN")
        .matches(/[a-zA-Z]{2}[0-9]{6}|[0-9]{8}/, "Enter valid CRN"),
      otherwise: yup.string(),
    }),
    name: yup
      .string()
      .required("This field is required")
      .matches(
        /^[\S]+(?:\s[\S]+)*$/,
        "Cannot start or end with whitespace characters"
      )
      .matches(/^[^!@#$%^&*()]+$/, "Special characters are not allowed"),
    industry: yup.string().required("This field is required").nullable(),
    address: yup.object({ ...addressValidationSchema }),
  }),
});

export const soleTraderSchema = yup.object({
  ...countryStructureSchema,
  soleTrader: yup.object({
    countryCode: yup.string(),
    utr: yup.string().when("countryCode", {
      is: "GB",
      then: yup
        .string()
        .matches(/^[0-9]*$/, "UTR cannot include letters")
        .nullable()
        .transform((o, c) => (o === "" ? null : c))
        .min(10, "Enter valid UTR")
        .max(10, "Enter valid UTR"),
      otherwise: yup.string(),
    }),
    firstAndMiddleName: yup
      .string()
      .required("This field is required")
      .matches(
        /^[\S]+(?:\s[\S]+)*$/,
        "Cannot start or end with whitespace characters"
      )
      .matches(/^[^!@#$%^&*()]+$/, "Special characters are not allowed"),
    surname: yup
      .string()
      .required("This field is required")
      .matches(
        /^[\S]+(?:\s[\S]+)*$/,
        "Cannot start or end with whitespace characters"
      )
      .matches(/^[^!@#$%^&*()]+$/, "Special characters are not allowed"),
    tradingName: yup
      .string()
      .required("This field is required")
      .matches(
        /^[\S]+(?:\s[\S]+)*$/,
        "Cannot start or end with whitespace characters"
      )
      .matches(/^[^!@#$%^&*()]+$/, "Special characters are not allowed"),
    address: yup.object({ ...addressValidationSchema }),
    industry: yup.string().required("This field is required").nullable(),
  }),
});

export const businessInfoLimitedSchema = yup.object({
  tradingName: yup
    .string()
    .required("This field is required")
    .matches(
      /^[\S]+(?:\s[\S]+)*$/,
      "Cannot start or end with whitespace characters"
    )
    .matches(/^[^!@#$%^&*()]+$/, "Special characters are not allowed"),
  tradingAddress: yup.object({
    ...addressValidationSchema,
  }),
});

export const emailSchema = yup.object({
  email: yup
    .string()
    .email("Email must be valid")
    .required("Email is required"),
});

export const passwordFormSchema = yup.object({
  password: yup
    .string()
    .required("required")
    .matches(/^.*[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?].*$/, "special_character")
    .matches(/^.*[0-9].*$/, "number")
    .matches(/^.*[a-z].*$/, "lowercase")
    .matches(/^.*[A-Z].*$/, "uppercase")
    .matches(/^\S*$/, "spaces")
    .matches(/^.{7,}$/, "min"),
  repeatPassword: yup
    .string()
    .required("Password confirmation is required")
    .oneOf([yup.ref("password"), null], "Passwords must match"),
});
